/**
 * AppearanceSection Component
 * Theme selector grid with color preview dots and animation settings
 */

import { TierBadge } from '@components/ui/TierBadge';
import type { Theme } from '@config/themes';
import type { User } from '@contexts/AuthContext';
import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import type { ReactElement } from 'react';

interface AppearanceSectionProps {
  // Data
  themeId: string;
  themes: Theme[];
  user: User | null;

  // Handlers
  canUseTheme: (themeId: string) => boolean;
  onThemeChange: (themeId: string) => void;
  onThemeUpgradeClick: () => void;
  onUpdatePreferences: (preferences: {
    enablePremiumAnimations?: boolean;
    showQuotaNotifications?: boolean;
    showBettingTips?: boolean;
  }) => Promise<void>;
}

export function AppearanceSection({
  themeId,
  themes,
  user,
  canUseTheme,
  onThemeChange,
  onThemeUpgradeClick,
  onUpdatePreferences,
}: AppearanceSectionProps): ReactElement {
  return (
    <section className="mb-6">
      <h3 className="text-lg font-medium text-theme-text mb-2">Appearance</h3>
      <div className="bg-theme-surface rounded-lg p-4 border border-theme-border space-y-4">
        {/* Theme selector */}
        <div>
          <label className="block text-sm font-medium text-theme-text mb-3">Theme</label>
          <div className="grid grid-cols-3 gap-2">
            {themes.map((theme) => {
              const locked = !canUseTheme(theme.id);
              const isActive = themeId === theme.id;
              return (
                <motion.button
                  type="button"
                  key={theme.id}
                  onClick={() => {
                    if (locked) {
                      onThemeUpgradeClick();
                      return;
                    }
                    onThemeChange(theme.id);
                  }}
                  whileHover={{ scale: locked ? 1 : 1.03 }}
                  whileTap={{ scale: locked ? 1 : 0.97 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  className={cn(
                    'p-2 rounded-lg border transition-colors text-left',
                    isActive
                      ? 'bg-theme-primary/20 border-theme-primary'
                      : 'bg-theme-bg border-theme-border hover:border-theme-text-tertiary',
                    locked && 'opacity-60',
                  )}
                  title={theme.description}
                >
                  <div className="flex items-center gap-2">
                    {/* Color preview dots */}
                    <div className="flex gap-0.5 shrink-0">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: theme.colors.primary }}
                      />
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: theme.colors.secondary }}
                      />
                    </div>
                    <span
                      className={cn(
                        'text-xs font-medium truncate',
                        isActive
                          ? 'text-theme-primary'
                          : locked
                            ? 'text-theme-text-tertiary'
                            : 'text-theme-text-secondary',
                      )}
                    >
                      {theme.name}
                    </span>
                    {locked && (
                      <span className="ml-auto shrink-0">
                        <TierBadge feature="theme_premium" />
                      </span>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Animation settings */}
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <div className="text-sm font-medium text-theme-text">Premium pick animations</div>
            <div className="text-xs text-theme-text-tertiary mt-0.5">
              Animate picks into risk tiers when generation completes
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              onUpdatePreferences({
                enablePremiumAnimations: !(user?.preferences?.enablePremiumAnimations ?? true),
              })
            }
            className={`relative w-10 h-5 shrink-0 rounded-full transition-colors ${
              (user?.preferences?.enablePremiumAnimations ?? true)
                ? 'bg-theme-primary'
                : 'bg-theme-border'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                (user?.preferences?.enablePremiumAnimations ?? true) ? 'translate-x-5' : ''
              }`}
            />
          </button>
        </label>

        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <div className="text-sm font-medium text-theme-text">Quota coin animations</div>
            <div className="text-xs text-theme-text-tertiary mt-0.5">
              Show a coin flip when picks, analysis, or tracking quota is used
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              onUpdatePreferences({
                showQuotaNotifications: !(user?.preferences?.showQuotaNotifications ?? true),
              })
            }
            className={`relative w-10 h-5 shrink-0 rounded-full transition-colors ${
              (user?.preferences?.showQuotaNotifications ?? true)
                ? 'bg-theme-primary'
                : 'bg-theme-border'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                (user?.preferences?.showQuotaNotifications ?? true) ? 'translate-x-5' : ''
              }`}
            />
          </button>
        </label>

        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <div className="text-sm font-medium text-theme-text">Show betting tips</div>
            <div className="text-xs text-theme-text-tertiary mt-0.5">
              Display helpful tips when generating and tracking picks
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              const newValue = !(user?.preferences?.showBettingTips ?? true);
              if (newValue) {
                // Re-enabling: clear seen tips so all reappear
                localStorage.removeItem('seenBettingTips');
              }
              onUpdatePreferences({ showBettingTips: newValue });
            }}
            className={`relative w-10 h-5 shrink-0 rounded-full transition-colors ${
              (user?.preferences?.showBettingTips ?? true) ? 'bg-theme-primary' : 'bg-theme-border'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                (user?.preferences?.showBettingTips ?? true) ? 'translate-x-5' : ''
              }`}
            />
          </button>
        </label>
      </div>
    </section>
  );
}
